import { Autocomplete, Grid,  TextField } from '@mui/material'

import {
  forwardRef,
  RefObject,
  SyntheticEvent,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
  useRef
} from 'react'
import { GlobalInfoContext, initialState } from 'contexts/GlobalStateContext'
import { RootState, useSelector, useDispatch } from 'redux/store'
import { PurposeType, Terms, TriContractPartyGroupRoleType, ConsortiumRole } from '@types'
import { FormikErrors, FormikTouched } from 'formik'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { generateID } from 'utils/generateMessageID'
import { REQUEST_MESSAGE_TYPES, Request } from 'schemas/request'
import { startLoading } from 'redux/slices/terms'

export type Types = {
  label: string
  value: string
}

type PurposeTypesProps = {
  setPurposeType: any
  purposeTypes: PurposeType[]
}
export const PurposeTypeSelection = ({ setPurposeType, purposeTypes }: PurposeTypesProps) => {
  const { setReqInfo, initiatedBy, type, id, applicants, beneficiaries } = useContext(GlobalInfoContext)
  const dispatch = useDispatch()
  const { onSocketSend } = useSocketConnection()
  const termsQuerySentRef = useRef<string | null>(null)
  const [purposeOptions, setPurposeOptions] = useState<{ label: string; id: string }[]>([
    { label: '', id: '' }
  ])

  useEffect(() => {
    const purposeList: { label: string; id: string }[] = []
    purposeTypes.forEach((pur: PurposeType) => {
      purposeList.push({
        label: pur?.purposeTypeDisplay ?? '',
        id: pur?.purposeTypeIdentifier ?? ''
      })
    })
    setPurposeOptions(purposeList)
  }, [purposeTypes])

  const handlePurposeTypeChange = (_e: SyntheticEvent, _value, _reason: string) => {
    if (_value && _value.id) {
      setPurposeType({
        label: _value?.label || '',
        id: _value?.id || ''
      })
      setReqInfo({
        ...initialState,
        initiatedBy,
        type: _value?.label,
        id: _value?.id
      })

      // Send QueryAvailableContractTerms with the data payload
      const purposeId = _value?.id


      if (purposeId) {
        
        // Dispatch loading state
        dispatch(startLoading())
        termsQuerySentRef.current = purposeId

        const requestTermsAndConditions: Request = {
          messageId: generateID(),
          messageType: REQUEST_MESSAGE_TYPES.QueryAvailableContractTerms,
          QueryAvailableContractTerms: {
            partyGroupRole: TriContractPartyGroupRoleType.APPLICANT,
            purposeTypeIdentifier: purposeId,
            applicants: [
              {
                ConsortiumRole: ConsortiumRole.SOLE_LE,
                onboarded: true
              }
            ],
            beneficiaries: [
              {
                legalEntityIdentifier: '',
                ConsortiumRole: ConsortiumRole.SOLE_LE,
                onboarded: true
              }
            ]
          }
        }
        onSocketSend(requestTermsAndConditions)
      }
    }
  }

  return (
    <Grid item md={4} lg={4}>
      <Autocomplete
        id="purpose-type-name"
        freeSolo={false}
        disableClearable={true}
        value={purposeOptions.find((opt) => opt.id === id) || null}
        options={purposeOptions}
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(opt, value) => opt?.id === value?.id}
        onInputChange={(_event, _param, reason) => {
          if (reason === 'clear') {
            setPurposeType({ label: '', id: '' })
            termsQuerySentRef.current = null
          }
        }}
        onChange={handlePurposeTypeChange}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ width: '99%' }}
            label="Purpose Type"
            placeholder="Select Purpose Type"
            InputLabelProps={{ shrink: true }}
            inputProps={{
                ...params.inputProps,
                readOnly: true
              }}
          />
        )}
      />
    </Grid>
  )
}

type TermsSelectionProps = {
  setIsTermsSelected: (value: boolean) => void
  handleEntityValue?: (value: Terms) => any
  disabled?: boolean
  touched?: FormikTouched<any>
  errors?: FormikErrors<any>
}

export const TermsAndConditionsSelection = forwardRef(
  (
    { setIsTermsSelected, handleEntityValue, disabled, touched, errors }: TermsSelectionProps,
    ref: RefObject<any>
  ) => {
    const { issuers: issu, purpose, setReqInfo, terms } = useContext(GlobalInfoContext)
    const [termsList, setTermsList] = useState<Terms[]>([])
    const [termsSelected, setTermsSelected] = useState<Terms>(
      terms || {
        termsIdentifier: '',
        termsDisplay: '',
        deedDisplay: '',
        deedIdentifier: ''
      }
    )

    const { issuers } = useSelector((state: RootState) => state.termsReducer)

    useEffect(() => {
      const list: Terms[] = []
      const filteredIssuer = issuers?.filter(
        (issuer) =>
          issuer?.issuerLegalEntity?.legalEntityIdentifier === issu[0]?.legalEntityIdentifier
      )
      filteredIssuer?.[0]?.terms?.forEach((contract) => {
        list.push({
          termsIdentifier: contract?.termsIdentifier,
          termsDisplay: contract?.termsDisplay,
          deedDisplay: contract?.deedDisplay,
          deedIdentifier: contract.deedIdentifier,
          templateTermsIdentifier: ''
        })
      })
      setTermsList(list)
    }, [issuers, issu])

    useEffect(() => {
      setTermsSelected(terms)
    }, [terms])
    useImperativeHandle(ref, () => ({
      resetTerms() {
        setTermsSelected({
          termsIdentifier: '',
          termsDisplay: '',
          deedDisplay: '',
          deedIdentifier: ''
        })
        setIsTermsSelected(false)
        setReqInfo({
          ...purpose,
          terms: {
            termsIdentifier: '',
            termsDisplay: '',
            deedDisplay: '',
            deedIdentifier: ''
          }
        })
      }
    }))

    return (
      <>
        <Autocomplete
          disablePortal
          id={`tandc-type-name`}
          getOptionLabel={(option: Terms) => option?.termsDisplay ?? ''}
          disabled={disabled}
          isOptionEqualToValue={(opts, value) => {
            // Always compare by termsIdentifier
            // Return false if value has no identifier (empty state)
            if (!value?.termsIdentifier) return false
            return opts?.termsIdentifier === value?.termsIdentifier
          }}
          options={termsList}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ width: '97%' }}
              label="Terms & Conditions (Mandatory)"
              placeholder="Select Applicable Terms..."
              InputLabelProps={{ shrink: true }}
              inputProps={{
                ...params.inputProps,
                readOnly: true
              }}
              error={Boolean(touched?.terms && errors?.terms)}
              helperText={touched?.terms && errors?.terms}
            />
          )}
          disableClearable={true}
          freeSolo={false}
          value={termsSelected}
          onChange={(_, value: Terms) => {
            setReqInfo({
              ...purpose,
              terms: {
                termsDisplay: value?.termsDisplay,
                termsIdentifier: value?.termsIdentifier,
                deedDisplay: value?.deedDisplay,
                deedIdentifier: value?.deedIdentifier
              }
            })
            setIsTermsSelected(true)
            setTermsSelected(value)
            handleEntityValue && handleEntityValue(value)
          }}
        />
      </>
    )
  }
)

